import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Command as CommandPrimitive } from "cmdk";
import { RiCheckLine, RiArrowDownSLine } from "@remixicon/react";
import { cn } from "../../../lib/utils";
import { TextField } from "../TextField";

export interface SearchableDropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SearchableDropdownProps {
  /** Currently selected value */
  value?: string;
  /** Callback when value changes */
  onValueChange?: (value: string) => void;
  /** List of options to display */
  options: SearchableDropdownOption[];
  /** Placeholder text for the search input */
  searchPlaceholder?: string;
  /** Message shown when no results match the search */
  emptyMessage?: string;
  /** Disable the dropdown */
  disabled?: boolean;
  /** Additional class name for the root element */
  className?: string;
  /** Additional class name for the dropdown content */
  contentClassName?: string;
  /** Alignment of the dropdown relative to trigger */
  align?: "start" | "center" | "end";
  /** Side of the trigger to render the dropdown */
  side?: "top" | "bottom" | "left" | "right";
  /** Side offset from the trigger */
  sideOffset?: number;
  /** Whether to avoid collisions with viewport edges (set false to keep fixed position) */
  avoidCollisions?: boolean;
  /** Custom trigger element - receives open state and selected option */
  trigger?: React.ReactNode;
  /** Render function for custom trigger with access to state */
  renderTrigger?: (props: {
    open: boolean;
    selectedOption?: SearchableDropdownOption;
    disabled?: boolean;
  }) => React.ReactNode;
  /** Maximum height of the options list */
  maxHeight?: number;
  /** Header content to display above the search input */
  header?: React.ReactNode;
  /** Whether to show the search input (default: true) */
  showSearch?: boolean;
}

/**
 * SearchableDropdown - A dropdown component with search functionality
 * and configurable trigger button.
 *
 * Uses shadcn/Radix Popover + cmdk Command components.
 * Colors are from capsule-preset.ts design tokens.
 *
 * The trigger can be customized in three ways:
 * 1. Default trigger (no props) - renders a styled button
 * 2. `trigger` prop - pass a static React element
 * 3. `renderTrigger` prop - pass a function with access to dropdown state
 */
function SearchableDropdown({
  value,
  onValueChange,
  options,
  searchPlaceholder = "Search...",
  emptyMessage = "No results found.",
  disabled = false,
  className,
  contentClassName,
  align = "start",
  side = "bottom",
  sideOffset = 4,
  avoidCollisions = true,
  trigger,
  renderTrigger,
  maxHeight = 200,
  header,
  showSearch = true,
}: SearchableDropdownProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");

  // Use case-insensitive comparison because cmdk normalizes values to lowercase
  const selectedOption = options.find(
    (option) => option.value.toLowerCase() === value?.toLowerCase(),
  );

  const filteredOptions = React.useMemo(() => {
    if (!showSearch || !searchQuery) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(query) ||
        option.value.toLowerCase().includes(query),
    );
  }, [options, searchQuery, showSearch]);

  const handleSelect = (selectedValue: string) => {
    // cmdk passes lowercased value, so find the original option to preserve casing
    const originalOption = options.find(
      (option) => option.value.toLowerCase() === selectedValue.toLowerCase(),
    );
    onValueChange?.(originalOption?.value ?? selectedValue);
    setOpen(false);
    setSearchQuery("");
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setSearchQuery("");
    }
  };

  // Determine which trigger to render
  const renderTriggerContent = () => {
    // Custom render function takes priority
    if (renderTrigger) {
      return renderTrigger({ open, selectedOption, disabled });
    }

    // Static trigger element
    if (trigger) {
      return trigger;
    }

    // Default trigger
    return (
      <DefaultTrigger
        open={open}
        selectedOption={selectedOption}
        disabled={disabled}
      />
    );
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <PopoverPrimitive.Trigger asChild disabled={disabled}>
        <div className={cn("inline-flex", className)}>
          {renderTriggerContent()}
        </div>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align={align}
          side={side}
          sideOffset={sideOffset}
          avoidCollisions={avoidCollisions}
          className={cn(
            // Base styles - using capsule-preset colors
            "z-50 w-[var(--radix-popover-trigger-width)] min-w-[200px] overflow-hidden",
            "rounded-lg border border-solid border-stroke-secondary bg-bg-primary p-2",
            "shadow-[0px_4px_8px_0px_rgba(0,0,0,0.12)]",
            // Animation styles (shadcn pattern)
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
            "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            contentClassName,
          )}
        >
          <CommandPrimitive
            className="flex h-full w-full flex-col overflow-hidden bg-bg-primary text-text-primary"
            shouldFilter={false}
          >
            {/* Optional Header */}
            {header && <div className="mb-2">{header}</div>}
            {/* Search Input - using TextField component */}
            {showSearch && (
              <TextField
                value={searchQuery}
                onValueChange={setSearchQuery}
                placeholder={searchPlaceholder}
                leadingIcon
                containerClassName="w-full"
                wrapperClassName="h-12"
              />
            )}

            {/* Options List - gap-2 (8px) between items per Figma */}
            {/* Custom scrollbar: 6px wide, rounded, icon-disabled color */}
            <CommandPrimitive.List
              className={cn(
                "flex flex-col gap-2 overflow-y-auto overflow-x-hidden",
                showSearch && "mt-2",
                // Custom scrollbar styles - using capsule-preset colors
                "[&::-webkit-scrollbar]:w-[6px]",
                "[&::-webkit-scrollbar-track]:bg-transparent",
                "[&::-webkit-scrollbar-thumb]:bg-icon-disabled",
                "[&::-webkit-scrollbar-thumb]:rounded-[100px]",
              )}
              style={{ maxHeight }}
            >
              {filteredOptions.length === 0 && (
                <div className="py-6 text-center text-sm text-text-tertiary">
                  {emptyMessage}
                </div>
              )}
              {filteredOptions.map((option) => {
                const isSelected =
                  value?.toLowerCase() === option.value.toLowerCase();
                return (
                  <CommandPrimitive.Item
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    onSelect={handleSelect}
                    className={cn(
                      // Base styles - using capsule-preset colors
                      // Figma: min-w-[90px], p-[4px], rounded-[4px], text-[16px], gap-[4px]
                      "relative flex w-full min-w-[90px] cursor-pointer select-none items-center gap-1",
                      "rounded p-2 text-base leading-5 text-text-primary outline-none",
                      // Hover state - bg appears on hover
                      "transition-colors hover:bg-bg-hover",
                      // Selected state (item with check mark) - bg appears
                      isSelected && "bg-bg-hover",
                      // Disabled state
                      "data-[disabled=true]:pointer-events-none data-[disabled=true]:text-text-disabled",
                    )}
                  >
                    <span>{option.label}</span>
                    {isSelected && (
                      // Figma: 24x24px blue check icon positioned at end with 4px gap
                      <RiCheckLine className="text-icon-blue" size={20} />
                    )}
                  </CommandPrimitive.Item>
                );
              })}
            </CommandPrimitive.List>
          </CommandPrimitive>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

/**
 * Default trigger button for the SearchableDropdown
 * Uses capsule-preset color tokens
 */
interface DefaultTriggerProps {
  open: boolean;
  selectedOption?: SearchableDropdownOption;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

function DefaultTrigger({
  open,
  selectedOption,
  disabled,
  placeholder = "Select option",
  className,
}: DefaultTriggerProps) {
  return (
    <button
      type="button"
      role="combobox"
      aria-expanded={open}
      disabled={disabled}
      className={cn(
        // Base styles - using capsule-preset colors
        "flex h-10 w-full items-center justify-between gap-2",
        "rounded-lg border border-solid bg-bg-primary px-3 py-2",
        "text-sm text-text-primary transition-colors",
        // Border color: dark when option is selected, default gray otherwise
        selectedOption
          ? "border-stroke-button-hover"
          : "border-stroke-secondary",
        // Hover & focus states
        "hover:border-stroke-blue focus:outline-none focus:ring-1 focus:ring-stroke-blue",
        // Disabled state
        "disabled:cursor-not-allowed disabled:bg-bg-disabled disabled:text-text-disabled",
        className,
      )}
    >
      <span className={cn("truncate", !selectedOption && "text-text-tertiary")}>
        {selectedOption ? selectedOption.label : placeholder}
      </span>
      <RiArrowDownSLine
        className={cn(
          "size-4 shrink-0 text-icon-secondary transition-transform",
          open && "rotate-180",
        )}
      />
    </button>
  );
}

SearchableDropdown.displayName = "SearchableDropdown";
DefaultTrigger.displayName = "SearchableDropdown.DefaultTrigger";

export { SearchableDropdown, DefaultTrigger as SearchableDropdownTrigger };
